import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ClipboardList, AlertTriangle, CheckCircle } from 'lucide-react'; // Import necessary icons
import { getObservationsByArea } from '../../services/observationAPI';
import toast from 'react-hot-toast';
import { apiConnector } from '../../services/apiconnector';
const BASE_URL = process.env.REACT_APP_API_URL; 
const AreaObservations = () => {
    const { areaId } = useParams();
    const [observations, setObservations] = useState([]);
    const [area, setArea] = useState(null);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem("token"); // Assuming token is needed for API calls

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // Fetch observations for the specific area
                const observationsRes = await getObservationsByArea(areaId);

                // Fetch area details using apiConnector
                const areaRes = await apiConnector(
                    "GET",
                    `${BASE_URL}/areas/get-areas/${areaId}`,
                    { Authorization: `Bearer ${token}` }
                );
            

                // Ensure observationsRes is an array or has an observations property that is an array
                const fetchedObservations = Array.isArray(observationsRes)
                    ? observationsRes
                    : (observationsRes.observations || []);

                setObservations(fetchedObservations.filter(obs => obs && typeof obs === 'object')); // Filter out invalid data
                setArea(areaRes); // Assuming area details are in areaRes.data

            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load observations');
                setObservations([]); // Ensure state is reset on error
                setArea(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [areaId, token]); // Depend on areaId and token
 
     const formatStatus = (status) => {
        if (typeof status !== 'string') return 'Unknown Status';
        return status.replace('-', ' ').replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
    };

    // Helper function to get status badge color
    const getStatusBadgeColor = (status) => {
        if (typeof status !== 'string') return 'bg-gray-100 text-gray-800';
        switch (status) {
            case 'waiting':
                return 'bg-amber-100 text-amber-800';
            case 'in-progress':
                return 'bg-purple-100 text-purple-800';
            case 'submitted':
                return 'bg-blue-100 text-blue-800';
            case 'completed':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!area) {
        return (
            <div className="text-center py-12">
                <h2 className="text-2xl font-bold text-gray-900">Area not found</h2>
                <p className="mt-2 text-gray-600">The area you're looking for doesn't exist or could not be loaded.</p>
            </div>
        );
    }

    // Calculate counts for unsafe act and unsafe condition
    const unsafeActCount = observations.filter(obs => obs.type === 'unsafe act').length;
    const unsafeConditionCount = observations.filter(obs => obs.type === 'unsafe condition').length;


    return (
        <div className="space-y-6 p-4"> {/* Added padding */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">{area?.name}</h1>
                <p className="text-gray-600">{area?.description}</p>
                 {/* Display HOD if available */}
                 {area.hod && (
                    <div className="text-sm text-gray-500 mt-1">
                        HOD: {area.hod.firstName} {area.hod.lastName} {/* Assuming HOD object is populated */}
                    </div>
                )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card bg-white">
                    <div className="flex items-center">
                        <div className="rounded-full bg-blue-100 p-3 mr-4">
                            <ClipboardList className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Observations</p>
                            <p className="text-2xl font-semibold">{observations.length}</p>
                        </div>
                    </div>
                </div>

                {/* Unsafe Act Stats */}
                <div className="card bg-white">
                    <div className="flex items-center">
                        <div className="rounded-full bg-amber-100 p-3 mr-4"> {/* Amber for Unsafe Act */}
                            <AlertTriangle className="h-6 w-6 text-amber-600" /> {/* AlertTriangle for Unsafe Act */}
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Unsafe Act Observations</p> {/* Updated Label */}
                            <p className="text-2xl font-semibold">
                                {unsafeActCount} {/* Use calculated count */}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Unsafe Condition Stats */}
                <div className="card bg-white">
                    <div className="flex items-center">
                        <div className="rounded-full bg-red-100 p-3 mr-4"> {/* Red for Unsafe Condition */}
                            <AlertTriangle className="h-6 w-6 text-red-600" /> {/* AlertTriangle for Unsafe Condition */}
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Unsafe Condition Observations</p> {/* Updated Label */}
                            <p className="text-2xl font-semibold">
                                {unsafeConditionCount} {/* Use calculated count */}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Observations List */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                    <h2 className="text-lg font-medium text-gray-900">Observations</h2>
                </div>
                <div className="border-t border-gray-200">
                    <ul className="divide-y divide-gray-200">
                        {observations.length === 0 ? (
                            <li className="px-4 py-5 sm:px-6 text-center text-gray-500">
                                No observations found for this area.
                            </li>
                        ) : (
                            observations.map((observation) => (
                                <li
                                    key={observation._id}
                                    // Apply background color based on observation type
                                    className={`px-4 py-5 sm:px-6 ${
                                        observation.type === 'unsafe act'
                                            ? 'bg-amber-50' // Light amber background
                                            : observation.type === 'unsafe condition'
                                            ? 'bg-red-50' // Light red background
                                            : '' // No specific background for other types
                                    }`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            {/* Display Observation Type and Status */}
                                            <div className="flex items-center space-x-2 mb-1">
                                                <p className={`text-sm font-medium ${
                                                    observation.type === 'unsafe act' ? 'text-amber-700' :
                                                    observation.type === 'unsafe condition' ? 'text-red-700' :
                                                    'text-gray-900' // Default color
                                                }`}>
                                                    {observation.type === 'unsafe act' ? 'Unsafe Act' :
                                                     observation.type === 'unsafe condition' ? 'Unsafe Condition' :
                                                     observation.type // Display raw type if not one of the known ones
                                                    }
                                                </p>
                                                {/* Display Status Badge */}
                                                {observation.status && (
                                                     <span className={`status-badge text-xs font-semibold px-2.5 py-0.5 rounded-full ${getStatusBadgeColor(observation.status)}`}>
                                                        {formatStatus(observation.status)}
                                                     </span>
                                                )}
                                            </div>
                                            {/* Display Description */}
                                            <p className="text-sm text-gray-700 mb-2">
                                                {observation.description}
                                            </p>
                                            {/* Display Observed By, Responsible Person, and Date */}
                                            <div className="mt-2 text-sm text-gray-500 space-y-0.5">
                                                {/* Use optional chaining and provide fallbacks */}
                                                <p>Observed by: {observation?.observedBy?.firstName} {observation?.observedBy?.lastName || 'N/A'}</p>
                                                {/* Check if responsiblePerson exists before accessing name */}
                                                <p>Responsible: {observation?.responsiblePerson ? `${observation.responsiblePerson.firstName} ${observation.responsiblePerson.lastName}` : 'N/A'}</p>
                                                <p>Date: {observation?.createdAt ? new Date(observation.createdAt).toLocaleDateString() : 'N/A'}</p>
                                                 {/* Display Category if it exists */}
                                                 {observation.category && (
                                                    <p>Category: {observation.category}</p>
                                                )}
                                            </div>
                                        </div>
                                        {/* You might want to add a link to view observation details here */}
                                        {/* <Link to={`/admin/observations/${observation._id}`} className="text-sm text-blue-600 hover:underline ml-4">View Details</Link> */}
                                    </div>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default AreaObservations;
